import { z } from 'zod';
export const CaseStatusSchema = z.enum([
    'ABIERTA',
    'EN_TRAMITE',
    'SUSPENDIDA',
    'ARCHIVADA',
    'GANADA',
    'PERDIDA',
    'ARCHIVADA_PROVISIONAL',
]);
export const CaseStatusLabel = {
    ABIERTA: 'Abierta',
    EN_TRAMITE: 'En trámite',
    SUSPENDIDA: 'Suspendida',
    ARCHIVADA: 'Archivada',
    GANADA: 'Ganada',
    PERDIDA: 'Perdida',
    ARCHIVADA_PROVISIONAL: 'Archivada provisional',
};
export const LegalAreaSchema = z.enum([
    'CIVIL',
    'PENAL',
    'LABORAL',
    'MERCANTIL',
    'ADMINISTRATIVO',
    'EXTRANJERIA',
    'FAMILIA',
    'CONCURSAL',
    'TRIBUTARIO',
    'ASESORIA',
    'OTRO',
]);
export const LegalAreaLabel = {
    CIVIL: 'Civil',
    PENAL: 'Penal',
    LABORAL: 'Laboral',
    MERCANTIL: 'Mercantil',
    ADMINISTRATIVO: 'Contencioso-administrativo',
    EXTRANJERIA: 'Extranjería',
    FAMILIA: 'Familia',
    CONCURSAL: 'Concursal',
    TRIBUTARIO: 'Tributario',
    ASESORIA: 'Asesoría jurídica',
    OTRO: 'Otra',
};
/** Áreas sin componente procesal: el expediente no lleva juzgado ni procurador. */
export const NON_LITIGATION_AREAS = ['ASESORIA'];
export const AssignmentRoleSchema = z.enum(['LEAD', 'COLABORADOR', 'PARALEGAL']);
export const AssignmentRoleLabel = {
    LEAD: 'Responsable',
    COLABORADOR: 'Colaborador',
    PARALEGAL: 'Paralegal',
};
const optionalString = (max = 200) => z.string().max(max).nullable().optional();
export const CreateCaseSchema = z.object({
    title: z.string().min(2, 'Título demasiado corto').max(300),
    clientId: z.string().min(1, 'Selecciona un cliente'),
    area: LegalAreaSchema,
    status: CaseStatusSchema.default('ABIERTA'),
    description: optionalString(5000),
    jurisdiction: optionalString(200),
    courtName: optionalString(200),
    courtFileNumber: optionalString(100),
    opposingParty: optionalString(300),
    openedAt: z.string().datetime().optional(),
    /** A4: emisor con el que se factura el expediente (si no, el del abogado o el del cliente). */
    issuerId: z.string().nullable().optional(),
});
export const UpdateCaseSchema = z.object({
    title: z.string().min(2).max(300).optional(),
    area: LegalAreaSchema.optional(),
    status: CaseStatusSchema.optional(),
    description: optionalString(5000),
    jurisdiction: optionalString(200),
    courtName: optionalString(200),
    courtFileNumber: optionalString(100),
    opposingParty: optionalString(300),
    openedAt: z.string().datetime().optional(),
    closedAt: z.string().datetime().nullable().optional(),
    /** F8: procurador actualmente asignado. */
    currentAgentId: z.string().nullable().optional(),
    issuerId: z.string().nullable().optional(),
});
export const CaseAssignmentSchema = z.object({
    id: z.string(),
    userId: z.string(),
    userFullName: z.string(),
    role: AssignmentRoleSchema,
    assignedAt: z.string().datetime(),
});
export const CaseListItemSchema = z.object({
    id: z.string(),
    code: z.string(),
    title: z.string(),
    status: CaseStatusSchema,
    area: LegalAreaSchema,
    clientId: z.string(),
    clientDisplayName: z.string(),
    openedAt: z.string().datetime(),
    closedAt: z.string().datetime().nullable(),
    assignedCount: z.number().int(),
    documentsCount: z.number().int(),
    courtName: z.string().nullable(),
    courtFileNumber: z.string().nullable(),
});
export const CaseDetailSchema = CaseListItemSchema.extend({
    description: z.string().nullable(),
    jurisdiction: z.string().nullable(),
    opposingParty: z.string().nullable(),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    assignments: z.array(CaseAssignmentSchema),
    canManage: z.boolean(),
    canWrite: z.boolean(),
    /** F8: procurador asignado. */
    currentAgentId: z.string().nullable(),
    currentAgentName: z.string().nullable(),
    /** A4: emisor propio del expediente (null = el del abogado, el del cliente o el del bufete). */
    issuerId: z.string().nullable(),
});
export const CaseListQuerySchema = z.object({
    q: z.string().max(200).optional(),
    status: CaseStatusSchema.optional(),
    area: LegalAreaSchema.optional(),
    clientId: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const CaseListResponseSchema = z.object({
    items: z.array(CaseListItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
export const AssignUserSchema = z.object({
    userId: z.string().min(1),
    role: AssignmentRoleSchema.default('COLABORADOR'),
});
export const CaseEventTypeSchema = z.enum([
    'CASE_CREATED',
    'STATUS_CHANGED',
    'ASSIGNMENT_ADDED',
    'ASSIGNMENT_REMOVED',
    'DOCUMENT_ADDED',
    'DOCUMENT_VERSION_ADDED',
    'NOTE',
    'COURT_NOTIFICATION',
    'CONTACT_LOG',
    'EMAIL_ARCHIVED',
    'CALENDAR_EVENT_ADDED',
    'CALENDAR_EVENT_REMOVED',
]);
export const CaseEventTypeLabel = {
    CASE_CREATED: 'Causa creada',
    STATUS_CHANGED: 'Cambio de estado',
    ASSIGNMENT_ADDED: 'Asignación',
    ASSIGNMENT_REMOVED: 'Baja de asignación',
    DOCUMENT_ADDED: 'Documento añadido',
    DOCUMENT_VERSION_ADDED: 'Nueva versión',
    NOTE: 'Nota',
    COURT_NOTIFICATION: 'Notificación juzgado',
    CONTACT_LOG: 'Comunicación',
    EMAIL_ARCHIVED: 'Correo archivado',
    CALENDAR_EVENT_ADDED: 'Evento añadido',
    CALENDAR_EVENT_REMOVED: 'Evento eliminado',
};
export const CaseEventSchema = z.object({
    id: z.string(),
    type: CaseEventTypeSchema,
    title: z.string(),
    description: z.string().nullable(),
    occurredAt: z.string().datetime(),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    documentId: z.string().nullable(),
});
export const CaseEventListResponseSchema = z.object({
    items: z.array(CaseEventSchema),
});
export const CreateCaseEventSchema = z.object({
    title: z.string().min(1).max(300),
    description: z.string().max(5000).optional().nullable(),
    type: z.enum(['NOTE', 'COURT_NOTIFICATION', 'CONTACT_LOG']).default('NOTE'),
    occurredAt: z.string().datetime().optional(),
});
//# sourceMappingURL=cases.js.map